import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { useQuery } from '@tanstack/react-query'
import Button from 'ext-ui/components/Button/Button'
import SingleDropDown from 'ext-ui/components/Dropdown/SingleDropdown/SingleDropdown'
import Processing from 'ext-ui/components/Processing/Processing'
import TextInput from 'ext-ui/components/TextInput/TextInput'
import { CountryCode, isSupportedCountry } from 'libphonenumber-js'
import React, { ChangeEvent, FormEvent, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  selectFetchingIssuerFromState,
  selectIssuerIdFromState,
} from 'src/app/RegisterAccount/redux/registerAccount.selectors'
import {
  amexCardTypes,
  amexIssuerCodes,
} from 'src/app/RegisterAccount/RegisterAccount'
import ReviewEnrollCard from 'src/app/ReviewEnrollCard/ReviewEnrollCard'
import {
  needsPhysicalCardTransactionsFeedFeature,
  resolveEnrollmentEligibility,
} from 'src/app/ReviewEnrollCard/reviewEnrollEligibility'
import AppName from 'src/components/AppName/AppName'
import RadioItems from 'src/components/RadioItems/RadioItems'
import SvgCheckLoD from 'src/components/Svgs/SvgCheckLoD'
import useAstrada from 'src/context/AstradaContext/useAstrada'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { Issuers, IssuerSchemaFilterFields, SupportedBrands } from 'src/enums'
import { useFormFields } from 'src/hooks'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import { hasEstablishedOnboardingSubscription } from 'src/models/organizations.models'
import { UserFeature } from 'src/models/user.models'
import { accountOverviewUrl, PRICING_ROUTE } from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import {
  countriesQueries,
  toCountryListModelMap,
  toCountryOptions,
} from 'src/queries/countries/countries.queries'
import { JPM_ISSUER_ID } from 'src/queries/issuers/issuerIntegrators/issuerIntegrators.models'
import {
  issuerIntegratorQueries,
  toIssuerIntegratorListModel,
} from 'src/queries/issuers/issuerIntegrators/issuerIntegrators.queries'
import { pricingPlanQueries } from 'src/queries/organizations/pricingPlans/pricingPlans.queries'
import { useQueryOrganizationSubscription } from 'src/queries/organizations/saas.queries'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { resetModalScroll } from 'src/utils/modalScroll.utils'
import { useWhitelabel } from 'src/utils/whitelabel.utils'
import { css } from 'styled-components'

import * as style from './OnboardingStepFour.style'

type OnboardingStepFourFields = {
  cardholderName: string
  country: string
  address1: string
  address2: string
  city: string
  province: string
  postal: string
  cardName: string
  /** For Amex only — card type (corporate/business/not_sure) */
  cardType: string
  [key: string]: string
}

interface OnboardingStepFourProps {
  currentStep: number
  totalSteps: number
  onResetScroll?: () => void
}

const services = [
  'Send secure virtual cards* to vendors, employees, and contractors',
  'Organize expenses for different teams, projects, clients, or locations',
  'Capture and auto-match receipts',
  'Automate expense tracking and reconciliation',
  'Sync transaction data to your accounting software',
]

const getPostalPlaceholder = (
  country: string,
  translate: (s: string) => string,
): string => {
  if (country === 'US') return '#####'
  if (country === 'CA') return '### ###'
  return translate('Enter Postal Code')
}

const OnboardingStepFour: React.FC<OnboardingStepFourProps> = ({
  currentStep,
  totalSteps,
  onResetScroll,
}) => {
  const locale = useSelector(selectNullSafeLocaleFromState)
  // Legacy behavior rendered with empty options/map until the fetch
  // succeeded; the `= []` / `= {}` defaults preserve that exactly (the
  // spinner below keys off the map being empty, as before).
  const { data: allCountryOptions = [] } = useQuery({
    ...countriesQueries.list(locale),
    select: toCountryOptions,
  })
  const { data: countrysMap = {} } = useQuery({
    ...countriesQueries.list(locale),
    select: toCountryListModelMap,
  })
  const user = useCurrentUser()
  const { whiteLabelIncludes } = useWhitelabel()
  // JPM has no BIN ranges, so the issuer id is fixed; otherwise it comes
  // from the BIN-range lookup (binRangeInfo.issuerId, set in Step 3).
  const binRangeIssuerId = useSelector(selectIssuerIdFromState)
  const issuerId = whiteLabelIncludes(SupportedBrands.Jpm)
    ? JPM_ISSUER_ID
    : binRangeIssuerId
  const issuerQuery = useQuery({
    ...issuerIntegratorQueries.detail(issuerId),
    select: toIssuerIntegratorListModel,
  })
  const selectedIssuer = issuerQuery.data
  const fetchingIssuer = useSelector(selectFetchingIssuerFromState)
  const translate = useTranslate()
  const history = useHistory()
  const astrada = useAstrada()
  const {
    data: userFeatures,
    isFetched: physicalCardTransactionsFeedIsFetched,
    isFetching: physicalCardTransactionsFeedIsFetching,
    isError: physicalCardTransactionsFeedIsError,
  } = useQueryUserFeatures([UserFeature.PHYSICAL_CARD_TRANSACTIONS_FEED], {
    enabled: Boolean(astrada.binData),
  })
  const physicalCardTransactionsFeedEnabled =
    !physicalCardTransactionsFeedIsError &&
    userFeatures?.[UserFeature.PHYSICAL_CARD_TRANSACTIONS_FEED] !== false
  const { analytics } = useAnalyticsContext()
  const [showReviewEnrollCard, setShowReviewEnrollCard] = useState(false)
  const saasEligibilityIssuerId = astrada.binData?.issuerId
  const { data: saasEligibility, isLoading: saasEligibilityLoading } = useQuery(
    pricingPlanQueries.saasEligibility(saasEligibilityIssuerId),
  )

  // Guard against routing an already-subscribed user back to pricing (e.g. a
  // user who selected a plan, dropped out before enrolling their card, and came
  // back). Only relevant when we'd otherwise route to pricing, so gate the
  // request (and the render wait) on having an eligibility issuer id AND SaaS
  // eligibility not having resolved to false — otherwise pricing is skipped
  // regardless and there's no reason to fetch or block on the subscription.
  const orgId = user?.organizationId
  const subscriptionGuardEnabled =
    Boolean(saasEligibilityIssuerId) && saasEligibility?.saasEnabled !== false
  const { data: subscriptionData, isFetched: subscriptionIsFetched } =
    useQueryOrganizationSubscription({
      orgId,
      enabled: subscriptionGuardEnabled,
    })
  const hasEstablishedSubscription = hasEstablishedOnboardingSubscription(
    subscriptionData?.subscription,
  )
  const shouldWaitForSubscription =
    subscriptionGuardEnabled && Boolean(orgId) && !subscriptionIsFetched

  const localeCountryGuess = locale?.slice(-2)?.toUpperCase()
  const defaultCountry: string =
    localeCountryGuess && isSupportedCountry(localeCountryGuess as CountryCode)
      ? localeCountryGuess
      : 'US'

  // Issuer-specific fields (e.g. account number for partner banks). The card
  // number / expiration / CVC are captured in Step 3, so filter those out here.
  const additionalIssuerFields =
    selectedIssuer?.schema?.filter(
      (field) =>
        field.name !== IssuerSchemaFilterFields.cardNumber &&
        field.name !== IssuerSchemaFilterFields.expires &&
        field.name !== IssuerSchemaFilterFields.cvc,
    ) || []

  const initialIssuerFieldValues = additionalIssuerFields.reduce<
    Record<string, string>
  >((acc, f) => {
    acc[f.name] = ''
    return acc
  }, {})

  const { fields, setFields, fieldsErrors, setFieldsErrors } =
    useFormFields<OnboardingStepFourFields>(
      {
        cardholderName: user?.fullName || '',
        country: defaultCountry,
        address1: '',
        address2: '',
        city: '',
        province: '',
        postal: '',
        cardName: '',
        cardType: '',
        ...initialIssuerFieldValues,
      },
      () => ({
        cardholderName: [],
        country: [],
        address1: [],
        address2: [],
        city: [],
        province: [],
        postal: [],
        cardName: [],
        cardType: [],
      }),
    )

  const isAmexIssuer = amexIssuerCodes.includes(selectedIssuer?.code as Issuers)

  const selectedAmexCardTypeIndex = amexCardTypes.findIndex(
    (cardType) => cardType.value === fields.cardType,
  )
  const [amexRadioIndex, setAmexRadioIndex] = useState<number>(
    selectedAmexCardTypeIndex >= 0 ? selectedAmexCardTypeIndex : -1,
  )

  const handleAmexCardTypeChange = (index: number): void => {
    setFields({
      ...fields,
      cardType: amexCardTypes[index].value,
    })
    setAmexRadioIndex(index)
  }

  useEffect((): void => {
    analytics?.track('[onboarding] additional card info', { action: 'viewed' })
  }, [])

  // Mirror AddressForm: respect the issuer's country restrictions when present.
  const countryOptions = (
    selectedIssuer?.restrictions?.country !== undefined
      ? (allCountryOptions || []).filter((opt) =>
          selectedIssuer.restrictions?.country.includes(opt.value),
        )
      : allCountryOptions || []
  ).map((c) => ({
    value: c.value,
    label: c.details.text,
  }))

  const selectedCountry = countrysMap?.[fields.country]

  // The form depends on two async loads: the country list (for address fields)
  // and the issuer integrator (for partner-bank-specific fields and the Amex
  // card-type radio). Hold the form behind a spinner until both arrive so the
  // user doesn't see fields pop in one at a time. The issuer lookup may
  // legitimately resolve with no issuer (BIN range with no `issuerId`); in
  // that case the form still renders — `additionalIssuerFields` is `[]` and
  // the Amex branch is inactive, matching the legacy flow's Extend behavior.
  // `isLoading` (not `isPending`): a disabled detail query — BIN range with
  // no `issuerId` — must not hold the form behind the spinner.
  const countriesLoaded = Object.keys(countrysMap).length > 0
  const issuerLoaded = !fetchingIssuer && !issuerQuery.isLoading
  const shouldShowSaasEligibilityLoader =
    Boolean(saasEligibilityIssuerId) && saasEligibilityLoading
  const shouldWaitForPhysicalCardTransactionsFeed =
    needsPhysicalCardTransactionsFeedFeature({
      binData: astrada.binData,
      saasEnabled: saasEligibility?.saasEnabled,
    }) &&
    (!physicalCardTransactionsFeedIsFetched ||
      physicalCardTransactionsFeedIsFetching)
  const isLoading =
    !countriesLoaded ||
    !issuerLoaded ||
    shouldShowSaasEligibilityLoader ||
    shouldWaitForPhysicalCardTransactionsFeed ||
    shouldWaitForSubscription
  const shouldRouteToPricing =
    Boolean(saasEligibilityIssuerId) &&
    saasEligibility?.saasEnabled !== false &&
    !hasEstablishedSubscription
  const submitButtonText = shouldRouteToPricing
    ? 'Next: Select Plan'
    : 'Next: Review Enrollment Terms'
  const resetScroll = (): void => {
    if (onResetScroll) {
      onResetScroll()
      return
    }
    resetModalScroll()
  }

  const validate = (): boolean => {
    const errors: Partial<Record<keyof OnboardingStepFourFields, string[]>> = {}

    if (!fields.cardholderName?.trim()) {
      errors.cardholderName = [translate('Must not be blank')]
    }
    if (!fields.country?.trim()) {
      errors.country = [translate('Must not be blank')]
    }
    if (!fields.cardName?.trim()) {
      errors.cardName = [translate('Must not be blank')]
    }

    selectedCountry?.fields?.forEach((countryField) => {
      const fieldName = countryField.name as keyof OnboardingStepFourFields
      const value = fields[fieldName]
      if (!countryField.optional && !value?.trim()) {
        errors[fieldName] = [translate('Must not be blank')]
      } else if (countryField.format && value) {
        const re = new RegExp(countryField.format)
        if (!re.test(value)) {
          errors[fieldName] = [translate('Invalid value')]
        }
      }
    })

    // Mirrors AddressForm:191-195 — the server schema marks `city` as
    // optional, but the legacy Additional Info flow has always enforced it.
    if (!fields.city?.trim()) {
      errors.city = [translate('Must not be blank')]
    }

    additionalIssuerFields.forEach((issuerField) => {
      if (issuerField.required && !fields[issuerField.name]?.trim()) {
        errors[issuerField.name as keyof OnboardingStepFourFields] = [
          translate('Must not be blank'),
        ]
      }
    })

    if (isAmexIssuer && !fields.cardType) {
      errors.cardType = [translate('Must not be blank')]
    }

    if (Object.keys(errors).length) {
      setFieldsErrors(errors)
      return false
    }
    setFieldsErrors({})
    return true
  }

  const submit = (): void => {
    if (!validate()) {
      analytics?.track('[onboarding] additional card info', {
        action: 'validation_failed',
      })
      return
    }

    const issuerData = additionalIssuerFields.reduce<Record<string, string>>(
      (acc, f) => {
        acc[f.name] = fields[f.name] ?? ''
        return acc
      },
      {},
    )

    astrada.updateState({
      cardData: astrada.cardData ? { ...astrada.cardData } : undefined,
      addressData: { ...fields },
      issuerData,
    })

    analytics?.track('[onboarding] additional card info', {
      action: 'completed',
      country: fields.country,
      has_address_2: !!fields.address2?.trim(),
      card_display_name_provided: !!fields.cardName?.trim(),
    })

    if (shouldRouteToPricing) {
      history.push(PRICING_ROUTE({ issuerId: saasEligibilityIssuerId }))
    } else {
      resetScroll()
      setShowReviewEnrollCard(true)
    }
  }

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault()
    submit()
  }

  const handleTextChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.currentTarget
    setFields({ ...fields, [name]: value })
  }

  // Mirror AddressForm: render country fields dynamically from the schema.
  // The Figma puts state + postal on one row; everything else (address1,
  // address2, city) is stacked above. Partition the schema fields accordingly.
  const countryFieldsAll = selectedCountry?.fields || []
  const stackedCountryFields = countryFieldsAll.filter(
    (f) => f.name !== 'province' && f.name !== 'postal',
  )
  const provinceSchemaField = countryFieldsAll.find(
    (f) => f.name === 'province',
  )
  const postalSchemaField = countryFieldsAll.find((f) => f.name === 'postal')

  const renderCountryField = (
    countryField: NonNullable<typeof selectedCountry>['fields'][number],
  ): React.ReactElement => {
    const { options, label, name } = countryField

    // Addressulator returns field and option labels already localized for the
    // requested locale, so render them directly instead of translating them
    // again. Placeholder copy is app-owned and must use stable catalog keys;
    // never use an Addressulator-localized label as a translation source key.

    if (options) {
      const provinceOptions = options.map((opt) => ({
        value: opt.code,
        label: opt.label,
      }))
      const selected = provinceOptions.find(
        (opt) => opt.value === fields.province,
      )
      const placeholder =
        fields.country === 'CA'
          ? translate('Select your Province')
          : fields.country === 'US'
            ? translate('Select your State')
            : translate('Select ${fieldLabel}', { fieldLabel: label })
      return (
        <SingleDropDown
          key={name}
          data-testid="state"
          selectedValue={selected}
          label={label}
          options={provinceOptions}
          searchable
          onSelect={({ value }): void => {
            setFields({ ...fields, province: value })
          }}
          errorText={fieldsErrors.province}
          placeholder={placeholder}
        />
      )
    }

    const getTextFieldPlaceholder = (): string => {
      if (fields.country !== 'CA' && fields.country !== 'US') {
        return translate('Enter ${fieldLabel}', { fieldLabel: label })
      }
      if (name === 'address1') return translate('Enter Billing Address 1')
      if (name === 'address2') return translate('Enter Billing Address 2')
      if (name === 'city') return translate('Enter City')
      return translate('Enter ${fieldLabel}', { fieldLabel: label })
    }

    return (
      <TextInput
        key={name}
        name={name}
        testId={name}
        label={label}
        value={fields[name] ?? ''}
        onChange={handleTextChange}
        placeholder={
          name === 'postal'
            ? getPostalPlaceholder(fields.country, translate)
            : getTextFieldPlaceholder()
        }
        errorText={fieldsErrors[name as keyof OnboardingStepFourFields]}
      />
    )
  }

  const sidePanel = (
    <div css={style.sideInfoPanel}>
      <div css={style.sideInfoInner}>
        <div css={style.sideInfoTitle}>
          {translate('Do more with your company credit card')}
        </div>
        <div css={style.sideInfoList}>
          {services.map((service) => (
            <div key={service} css={style.sideInfoServiceRow}>
              <span css={style.sideInfoServiceIcon}>
                <SvgCheckLoD />
              </span>
              <span css={style.sideInfoServiceText}>{translate(service)}</span>
            </div>
          ))}
          <div css={style.sideInfoDisclaimer}>
            {translate(
              '*Virtual card capabilities available through select bank partners.',
            )}
          </div>
        </div>
      </div>
    </div>
  )

  const reviewSubmissionData =
    astrada.binData && astrada.cardData && astrada.addressData
      ? {
          binData: {
            ...astrada.binData,
            eligibility: resolveEnrollmentEligibility({
              binData: astrada.binData,
              physicalCardTransactionsFeedEnabled,
              saasEnabled: saasEligibility?.saasEnabled,
            }),
          },
          cardData: astrada.cardData,
          addressData: astrada.addressData,
          issuerData: astrada.issuerData,
        }
      : undefined

  if (showReviewEnrollCard && reviewSubmissionData) {
    return (
      <div css={style.reviewPane} data-testid="onboarding-step-four-review">
        <ReviewEnrollCard
          submissionData={reviewSubmissionData}
          goBackText={translate('Back to Additional Info')}
          onBack={() => {
            resetScroll()
            setShowReviewEnrollCard(false)
          }}
          onVerifyComplete={(cardId) => {
            if (shouldRouteToPricing) {
              history.push(
                PRICING_ROUTE({
                  cardId,
                  issuerId: astrada.binData?.issuerId,
                }),
              )
            } else {
              history.push(accountOverviewUrl(cardId))
            }
            astrada.reset()
          }}
        />
      </div>
    )
  }

  return (
    <div
      data-testid="onboarding-step-four"
      css={css`
        display: flex;
        flex-grow: 1;
      `}
    >
      <div css={style.formPane}>
        <div css={style.stepEyebrow}>
          {translate('Step ${currentStep} of ${totalSteps}', {
            currentStep: currentStep.toString(),
            totalSteps: totalSteps.toString(),
          })}
        </div>
        <h2
          css={css`
            ${getFontCss(
              32,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )};
            font-weight: 500;
            margin: 0 0 20px 0;
          `}
        >
          {translate('Add Additional Card Info')}
        </h2>
        <p
          css={css`
            ${getFontCss(
              16,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.regular,
            )};
            margin: 0 0 28px 0;
            max-width: 490px;
          `}
        >
          {translate(
            'Your credit card requires a little more info to connect to ${appName}. Please fill that out.',
            { appName: <AppName /> },
          )}
        </p>
        {isLoading ? (
          <div css={style.loadingContainer} data-testid="step-four-loading">
            <Processing />
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            {isAmexIssuer && (
              <div css={style.amexCardTypeBlock}>
                <div css={style.amexCardTypeTitle}>
                  {translate('Card Type')}
                </div>
                <div css={style.amexCardTypeMicrocopy}>
                  {translate(
                    'Telling us your card type will help us speed up this registration',
                  )}
                </div>
                <RadioItems
                  items={amexCardTypes.map((cardType) => (
                    <span
                      key={cardType.value}
                      data-testid={`radio-button-${cardType.value.replace(/_/g, '-')}`}
                    >
                      <p
                        css={css`
                          ${getFontCss(
                            14,
                            styleConstants.colors.gray_darkest,
                            styleConstants.fontFamilies.regular,
                          )};
                        `}
                      >
                        {cardType.fieldText}
                      </p>
                    </span>
                  ))}
                  currentIndex={amexRadioIndex}
                  setIndex={handleAmexCardTypeChange}
                  horizontalAlign
                  staticOnHover
                  noBorder
                  alignStart
                  gap={'40px'}
                  errors={fieldsErrors.cardType}
                  ariaLabels={amexCardTypes.map((ct) =>
                    typeof ct.fieldText === 'string'
                      ? translate(ct.fieldText)
                      : translate(ct.fieldText.props.source),
                  )}
                />
              </div>
            )}
            <div css={style.fieldGroup}>
              <TextInput
                name="cardholderName"
                testId="cardholderName"
                label={translate('Cardholder Name')}
                value={fields.cardholderName}
                onChange={handleTextChange}
                placeholder={translate('Enter Cardholder Name')}
                errorText={fieldsErrors.cardholderName}
              />
              <SingleDropDown
                data-testid="country"
                selectedValue={countryOptions.find(
                  (opt) => opt.value === fields.country,
                )}
                label={translate('Country')}
                options={countryOptions}
                searchable
                onSelect={({ value }): void => {
                  setFields({ ...fields, country: value, province: '' })
                }}
                errorText={fieldsErrors.country}
                placeholder={translate('Enter your country')}
              />
              {stackedCountryFields.map(renderCountryField)}
              <div css={style.fieldRow}>
                {provinceSchemaField && renderCountryField(provinceSchemaField)}
                {postalSchemaField && renderCountryField(postalSchemaField)}
              </div>
              {additionalIssuerFields.map((issuerField) => (
                <TextInput
                  key={issuerField.name}
                  name={issuerField.name}
                  testId={issuerField.name}
                  label={translate(issuerField.label)}
                  value={fields[issuerField.name] ?? ''}
                  onChange={handleTextChange}
                  placeholder={translateSingleString(
                    locale,
                    'Enter ${fieldLabel}',
                    { fieldLabel: issuerField.label },
                  )}
                  microcopy={issuerField.description}
                  errorText={
                    fieldsErrors[
                      issuerField.name as keyof OnboardingStepFourFields
                    ]
                  }
                />
              ))}
            </div>
            <div css={style.dividerLine} />
            <div css={style.fieldGroup}>
              <TextInput
                name="cardName"
                testId="cardName"
                maxLength={255}
                label={translate('Card Display Name')}
                value={fields.cardName}
                onChange={handleTextChange}
                placeholder={translate('Enter Card Display Name')}
                microcopy={translate(
                  "Your card's display name appears at the top of the card detail screen, as well as anywhere that refers to the card. You can update it at any time.",
                )}
                errorText={fieldsErrors.cardName}
              />
            </div>
            <div css={style.submitRow}>
              <Button type="submit" data-testid="next-button">
                {translate(submitButtonText)}
              </Button>
            </div>
          </form>
        )}
      </div>
      {sidePanel}
    </div>
  )
}

export default OnboardingStepFour
